import React, { useEffect, useState } from 'react';
import { FaYoutube, FaFacebookF, FaHeart, FaUserFriends, FaExternalLinkAlt, FaPlay } from 'react-icons/fa';
import axios from 'axios';

function SocialMediaSection() {
  const [socialData, setSocialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFallback, setShowFallback] = useState(false);

  const API_BASE_URL = 'https://aman-life-style-bd-main-server.sparkx-ai.shop';

  useEffect(() => {
    // Fetch social media data from API
    const fetchSocialMediaData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/social-media-embed`);

        if (response.data.success && response.data.data) {
          setSocialData(response.data.data);
        } else {
          setSocialData(null);
        }
      } catch (err) {
        console.error('Error fetching social media data:', err);
        setError('Failed to load social media content');
        setSocialData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSocialMediaData();
  }, []);

  // Don't render if loading
  if (loading) {
    return (
      <div className="w-full bg-white py-8 px-4 md:px-6">
        <div className="max-w-7xl mx-auto flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  // Don't render if not active or no data
  if (!socialData || !socialData.is_active) {
    return null;
  }

  // Don't render if no URLs provided
  if (!socialData.youtube_embed_url && !socialData.facebook_page_url) {
    return null;
  }

  // Extract Facebook page username from URL
  const getFacebookPageId = (url) => {
    if (!url) return '';
    const match = url.match(/facebook\.com\/([^/?]+)/);
    return match ? match[1] : '';
  };

  const fbPageId = getFacebookPageId(socialData.facebook_page_url);

  // Custom Facebook Preview Component (Fallback)
  const FacebookPreview = () => (
    <div className="bg-white w-full h-full flex flex-col" style={{ fontFamily: "'Assistant', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      {/* Cover Photo Placeholder */}
      <div className="w-full h-32 bg-gradient-to-r from-blue-400 to-blue-600 relative">
        <div className="absolute inset-0 bg-black opacity-20"></div>
      </div>

      {/* Profile Section */}
      <div className="px-4 -mt-12 relative">
        <div className="w-24 h-24 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center">
          <FaFacebookF className="text-[#1877F2] text-3xl" />
        </div>
      </div>

      <div className="px-4 mt-3 pb-4 border-b">
        <h3 className="text-lg font-bold text-gray-900">Visit Our Facebook Page</h3>
        <p className="text-sm text-gray-600 mt-1">@{fbPageId}</p>

        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <FaUserFriends className="text-[#1877F2]" />
            <span>Follow us</span>
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <FaHeart className="text-red-500" />
            <span>Like our page</span>
          </div>
        </div>

        <a
          href={socialData.facebook_page_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 w-full bg-[#1877F2] text-white py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-[#0e62d0] transition-colors"
        >
          <span>Open Facebook Page</span>
          <FaExternalLinkAlt className="text-sm" />
        </a>
      </div>

      {/* Timeline Preview */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="text-center text-gray-500 text-sm mb-4">
          Recent Posts
        </div>

        {/* Post Placeholder 1 */}
        <div className="mb-4 p-3 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-gray-200"></div>
            <div className="flex-1">
              <div className="h-3 bg-gray-200 rounded w-24 mb-1"></div>
              <div className="h-2 bg-gray-100 rounded w-16"></div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-2 bg-gray-100 rounded w-full"></div>
            <div className="h-2 bg-gray-100 rounded w-5/6"></div>
          </div>
        </div>

        {/* Post Placeholder 2 */}
        <div className="mb-4 p-3 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-gray-200"></div>
            <div className="flex-1">
              <div className="h-3 bg-gray-200 rounded w-24 mb-1"></div>
              <div className="h-2 bg-gray-100 rounded w-16"></div>
            </div>
          </div>
          <div className="space-y-2 mb-2">
            <div className="h-2 bg-gray-100 rounded w-full"></div>
            <div className="h-2 bg-gray-100 rounded w-4/6"></div>
          </div>
          <div className="w-full h-32 bg-gray-200 rounded"></div>
        </div>

        <div className="text-center">
          <a
            href={socialData.facebook_page_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#1877F2] text-sm font-semibold hover:underline"
          >
            View More on Facebook →
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full bg-gradient-to-b from-white via-gray-50 to-white py-16 px-4 md:px-6 overflow-hidden" style={{ fontFamily: "'Assistant', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <h2
            className="text-3xl md:text-4xl font-bold text-[#232F3E] mb-3"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            Connect With Us
          </h2>
          <p className="text-gray-600 text-base md:text-lg">Stay updated with our latest content and exclusive offers</p>
          <div className="w-24 h-1 bg-[#FF9900] mx-auto mt-4 rounded-full"></div>
        </div>

        {/* Main Grid Layout - Increased gap for desktop */}
        <div className="flex flex-col lg:flex-row gap-12 justify-center items-center lg:gap-40 xl:gap-56 2xl:gap-72">

          {/* YouTube Phone Mockup */}
          {socialData.youtube_embed_url && (
            <div className="relative flex justify-center items-center perspective-2000 lg:justify-end">
              {/* Floating decoration */}
              <div className="absolute -top-8 -left-8 w-32 h-32 bg-red-500 rounded-full opacity-10 blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-orange-500 rounded-full opacity-10 blur-3xl animate-pulse delay-1000"></div>

              {/* 3D Phone Container - Standing position */}
              <div className="relative phone-3d-left transition-all duration-700 ease-out" style={{ transformStyle: 'preserve-3d' }}>
                {/* Phone Shadow - Vertical shadow for standing position */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-[70%] h-6 bg-black opacity-30 blur-xl rounded-full"></div>

                {/* Phone Frame */}
                <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black rounded-[3rem] p-3 shadow-2xl" style={{ width: '340px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.1)' }}>
                  {/* Phone Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-7 bg-black rounded-b-3xl z-10 flex items-center justify-center gap-2">
                    <div className="w-16 h-1 bg-gray-800 rounded-full"></div>
                  </div>

                  {/* Phone Screen */}
                  <div className="relative bg-white rounded-[2.5rem] overflow-hidden shadow-inner" style={{ height: '680px' }}>
                    {/* YouTube Header */}
                    <div className="bg-gradient-to-r from-[#FF0000] to-[#CC0000] p-4 flex items-center justify-between relative z-20 shadow-md">
                      <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded-xl shadow-lg">
                          <FaYoutube className="text-[#FF0000] text-2xl" />
                        </div>
                        <div>
                          <h3 className="text-white font-bold text-base">Latest Videos</h3>
                          <p className="text-xs text-red-100">Watch our content</p>
                        </div>
                      </div>
                      <a
                        href={socialData.youtube_embed_url.replace('/embed/', '/watch?v=')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white text-[#FF0000] px-4 py-2 rounded-xl text-xs font-bold hover:bg-red-50 transition-colors shadow-md flex items-center gap-1"
                      >
                        <FaPlay className="text-xs" />
                        <span>Subscribe</span>
                      </a>
                    </div>

                    {/* YouTube Video Embed */}
                    <div className="relative bg-black" style={{ height: 'calc(680px - 64px)' }}>
                      <iframe
                        className="absolute top-0 left-0 w-full h-full"
                        src={socialData.youtube_embed_url}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>

                  {/* Phone Volume Buttons */}
                  <div className="absolute -left-1 top-32 w-1 h-12 bg-gray-700 rounded-l-lg"></div>
                  <div className="absolute -left-1 top-48 w-1 h-16 bg-gray-700 rounded-l-lg"></div>

                  {/* Phone Power Button */}
                  <div className="absolute -right-1 top-36 w-1 h-16 bg-gray-700 rounded-r-lg"></div>

                  {/* Phone Bottom Bar */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1.5 bg-gray-700 rounded-full"></div>
                </div>

                {/* Reflection Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-[3rem] pointer-events-none" style={{ transform: 'translateZ(1px)' }}></div>
              </div>
            </div>
          )}

          {/* Facebook Phone Mockup */}
          {socialData.facebook_page_url && (
            <div className="relative flex justify-center items-center perspective-2000 lg:justify-start">
              {/* Floating decoration */}
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-blue-500 rounded-full opacity-10 blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-purple-500 rounded-full opacity-10 blur-3xl animate-pulse delay-1000"></div>

              {/* 3D Phone Container - Standing position */}
              <div className="relative phone-3d-right transition-all duration-700 ease-out" style={{ transformStyle: 'preserve-3d' }}>
                {/* Phone Shadow - Vertical shadow for standing position */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-[70%] h-6 bg-black opacity-30 blur-xl rounded-full"></div>

                {/* Phone Frame */}
                <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black rounded-[3rem] p-3 shadow-2xl" style={{ width: '340px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.1)' }}>
                  {/* Phone Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-7 bg-black rounded-b-3xl z-10 flex items-center justify-center gap-2">
                    <div className="w-16 h-1 bg-gray-800 rounded-full"></div>
                  </div>

                  {/* Phone Screen */}
                  <div className="relative bg-white rounded-[2.5rem] overflow-hidden shadow-inner" style={{ height: '680px' }}>
                    {/* Facebook Header */}
                    <div className="bg-gradient-to-r from-[#1877F2] to-[#0e62d0] p-4 flex items-center justify-between relative z-20 shadow-md">
                      <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded-xl shadow-lg">
                          <FaFacebookF className="text-[#1877F2] text-2xl" />
                        </div>
                        <div>
                          <h3 className="text-white font-bold text-base">Facebook</h3>
                          <p className="text-xs text-blue-100">Follow our page</p>
                        </div>
                      </div>
                      <a
                        href={socialData.facebook_page_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white text-[#1877F2] px-4 py-2 rounded-xl text-xs font-bold hover:bg-blue-50 transition-colors shadow-md flex items-center gap-1"
                      >
                        <FaHeart className="text-xs" />
                        <span>Like</span>
                      </a>
                    </div>

                    {/* Facebook Content */}
                    <div className="bg-white w-full" style={{ height: 'calc(680px - 64px)' }}>
                      {showFallback ? (
                        <FacebookPreview />
                      ) : (
                        <iframe
                          src={`https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(socialData.facebook_page_url)}&tabs=timeline&width=500&height=616&small_header=false&adapt_container_width=false&hide_cover=false&show_facepile=true`}
                          width="340"
                          height="616"
                          style={{
                            border: 'none',
                            overflow: 'hidden',
                            width: '100%',
                            height: '100%'
                          }}
                          scrolling="no"
                          frameBorder="0"
                          allowFullScreen={true}
                          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                        ></iframe>
                      )}
                    </div>
                  </div>

                  {/* Phone Volume Buttons */}
                  <div className="absolute -left-1 top-32 w-1 h-12 bg-gray-700 rounded-l-lg"></div>
                  <div className="absolute -left-1 top-48 w-1 h-16 bg-gray-700 rounded-l-lg"></div>

                  {/* Phone Power Button */}
                  <div className="absolute -right-1 top-36 w-1 h-16 bg-gray-700 rounded-r-lg"></div>

                  {/* Phone Bottom Bar */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1.5 bg-gray-700 rounded-full"></div>
                </div>

                {/* Reflection Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-[3rem] pointer-events-none" style={{ transform: 'translateZ(1px)' }}></div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.1;
          }
          50% {
            opacity: 0.2;
          }
        }

        .animate-pulse {
          animation: pulse 3s ease-in-out infinite;
        }

        .delay-1000 {
          animation-delay: 1s;
        }

        .perspective-2000 {
          perspective: 2000px;
        }

        /* YouTube Phone - Standing Left (facing right) */
        .phone-3d-left {
          transform: rotateY(25deg);
        }

        /* Facebook Phone - Standing Right (facing left) */
        .phone-3d-right {
          transform: rotateY(-25deg);
        }

        /* Mobile - No rotation */
        @media (max-width: 1024px) {
          .phone-3d-left,
          .phone-3d-right {
            transform: rotateY(0deg) !important;
          }
        }

        /* Desktop Hover Effects - Straighten animation */
        @media (min-width: 1024px) {
          .phone-3d-left:hover {
            transform: rotateY(0deg) scale(1.05);
            filter: drop-shadow(0 30px 60px rgba(0, 0, 0, 0.4));
          }

          .phone-3d-right:hover {
            transform: rotateY(0deg) scale(1.05);
            filter: drop-shadow(0 30px 60px rgba(0, 0, 0, 0.4));
          }
        }

        /* Smooth hover transition for mobile */
        @media (max-width: 1024px) {
          .phone-3d-left:active,
          .phone-3d-right:active {
            transform: scale(0.98);
          }
        }
      `}</style>
    </div>
  );
}

export default SocialMediaSection;